/*
 * Birthday in the Rivo account widget, and the read-only Email row.
 *
 * The birthday field is Rivo's own preference question (key "birth_date", a
 * date with the year). Rivo renders it in the profile and in the preferences
 * form and saves it to the customer metafield app--4767275--rivo.birth_date,
 * which Shopify Flow and Klaviyo read. The question itself is managed in the
 * Rivo admin (Accounts → Preferences); nothing here depends on its id.
 *
 * This script adds what Rivo does not do:
 *   - once answered, the birthday is locked: the profile row stops linking to
 *     the form and the date input turns read-only. A birthday that can be
 *     changed at will hands out birthday perks at will;
 *   - the answer is copied, as MM-DD, into Rivo's own dob field. Rivo's
 *     "Customer birthday" trigger in Shopify Flow and its "Birthday Captured"
 *     event for Klaviyo run on dob, not on the preference.
 *
 * The lock is in the interface only — the Rivo admin and API can still change
 * the date. That is enough against the case it exists for.
 *
 * Design: docs/superpowers/specs/2026-09-24-birthday-capture-design.md
 */
(function (root) {
  /* Rivo's date preference answers come as YYYY-MM-DD. Read the parts off the
     string: going through Date would shift the day for visitors west of UTC.
     dob is written as MM-DD, the format Rivo's own birthday form sends: the
     endpoint answers {"success":false} to MM/DD and keeps dob unchanged. */
  function dobFromIso(iso) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
    if (!match) return null;
    var month = parseInt(match[2], 10);
    var day = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return match[2] + '-' + match[3];
  }

  function findAnswer(preferences, questionId) {
    if (!Array.isArray(preferences) || questionId === null || questionId === undefined) return null;
    for (var i = 0; i < preferences.length; i += 1) {
      var answer = preferences[i];
      if (answer && String(answer.preference_question_id) === String(questionId) && answer.value) {
        return String(answer.value);
      }
    }
    return null;
  }

  /* dob comes back as MM-DD, or as M/D for dates saved before; compare as
     numbers so "3/7" equals "03-07". */
  function sameDob(a, b) {
    var ma = /^(\d{1,2})[-\/](\d{1,2})$/.exec(String(a || '').trim());
    var mb = /^(\d{1,2})[-\/](\d{1,2})$/.exec(String(b || '').trim());
    if (!ma || !mb) return false;
    return parseInt(ma[1], 10) === parseInt(mb[1], 10) && parseInt(ma[2], 10) === parseInt(mb[2], 10);
  }

  function dobTarget(iso, currentDob) {
    var target = dobFromIso(iso);
    if (!target || sameDob(target, currentDob)) return null;
    return target;
  }

  /* Nothing is decided until Rivo has loaded the customer: an empty store
     would read as "no dob yet" and send a write that may not be needed. */
  function dobSyncDecision(iso, customer) {
    if (!customer || !customer.id) return { ready: false, target: null };
    return { ready: true, target: dobTarget(iso, customer.dob) };
  }

  var api = {
    dobFromIso: dobFromIso,
    findAnswer: findAnswer,
    dobTarget: dobTarget,
    dobSyncDecision: dobSyncDecision
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.NomBirthday = api;

  if (typeof document === 'undefined') return;

  var BIRTHDAY_KEY = 'birth_date';
  var PANEL = '#rivo-tabpanel-rivo-profile';
  var PROFILE_ROWS = PANEL + ' a.rivo-preference-item[href="#rivo-preferences"]';
  var FORM_INPUTS = '.rivo-preferences-page input[id^="preference_"]';
  var READONLY = 'data-nom-readonly';
  var LOCKED = 'data-nom-birthday-locked';
  var NOTE_CLASS = 'nom-birthday-note';
  var NOTE_TEXT = 'Your birthday can only be set once.';

  /* One dob write per answer per page load: a failing write is not retried in
     a loop, and a successful one is not repeated on every re-render. */
  var syncedFor = null;

  function alpine() {
    return root.RivoProfileAlpine || null;
  }

  function store(name) {
    try {
      return alpine() ? alpine().store(name) : null;
    } catch (e) {
      return null;
    }
  }

  function questionOf(el) {
    try {
      var data = alpine() && alpine().$data(el);
      return (data && data.question) || null;
    } catch (e) {
      return null;
    }
  }

  function isBirthday(el) {
    var question = questionOf(el);
    return !!question && question.key === BIRTHDAY_KEY;
  }

  /* The question is found by its key wherever Rivo has rendered it — the
     profile row or the form input. Both carry the question in their Alpine
     scope. */
  function birthdayQuestionId() {
    var nodes = document.querySelectorAll(PROFILE_ROWS + ', ' + FORM_INPUTS);
    for (var i = 0; i < nodes.length; i += 1) {
      var question = questionOf(nodes[i]);
      if (question && question.key === BIRTHDAY_KEY) return question.id;
    }
    return null;
  }

  function lockRow() {
    var rows = document.querySelectorAll(PROFILE_ROWS);
    for (var i = 0; i < rows.length; i += 1) {
      if (!isBirthday(rows[i])) continue;
      rows[i].setAttribute(READONLY, '');
      rows[i].removeAttribute('href');
    }
  }

  /* readOnly, not disabled: a disabled input may be left out when Rivo saves
     the form, and the answer must survive a save of the name or phone. */
  function lockInput(questionId) {
    var input = document.getElementById('preference_' + questionId);
    if (!input || input.hasAttribute(LOCKED)) return;
    input.readOnly = true;
    input.setAttribute('aria-readonly', 'true');
    input.setAttribute('tabindex', '-1');
    input.setAttribute(LOCKED, '');

    /* After the field's wrapper, not the input: the wrapper is a flex row and
       the note would sit beside the date instead of under it. */
    var note = document.createElement('p');
    note.className = NOTE_CLASS;
    note.textContent = NOTE_TEXT;
    (input.closest('.rivo-form-field') || input).insertAdjacentElement('afterend', note);
  }

  function syncDob(answer) {
    if (syncedFor === answer) return;

    /* Not ready yet — the next mutation brings another pass. The attempt is
       only spent once there is something to decide with. */
    var customer = store('rivo_profile_customer');
    var decision = dobSyncDecision(answer, customer);
    if (!decision.ready || !root.RivoJS || !root.RivoJS.updateCustomerDob) return;

    syncedFor = answer;
    var target = decision.target;
    if (!target) return;

    root.RivoJS.updateCustomerDob(target)
      .then(function (ok) {
        if (ok && customer) customer.dob = target;
      })
      .catch(function () {
        /* Left for the next page load. */
      });
  }

  /*
   * Rivo will not let the login email be changed — its own preferences form
   * ships that field disabled, with "Email used for login cannot be updated"
   * under it. In the profile card, though, the row looks exactly like Name and
   * offers the same Edit affordance, which promises something that cannot
   * happen. Marking it lets the stylesheet render it as read-only.
   *
   * The row is found by its value rather than its label, because Rivo gives
   * Email and Name identical markup and the label text is translated.
   */
  function markEmailReadOnly() {
    var rows = document.querySelectorAll(PANEL + ' .rivo-preference-item');
    for (var i = 0; i < rows.length; i += 1) {
      var row = rows[i];
      if (row.hasAttribute(READONLY)) continue;

      var value = row.querySelector('.rivo-preference-item-value');
      if (value && /^\S+@\S+\.\S+$/.test(value.textContent.trim())) {
        row.setAttribute(READONLY, '');
        row.removeAttribute('href');
      }
    }
  }

  function apply() {
    markEmailReadOnly();

    var questionId = birthdayQuestionId();
    if (questionId === null) return;

    var answer = findAnswer(store('rivo_customer_preferences'), questionId);
    if (!answer) return;

    lockRow();
    lockInput(questionId);
    syncDob(answer);
  }

  function start() {
    if (!alpine()) return;
    apply();

    var content = document.querySelector('.rivo-slideout__content');
    if (!content || content.hasAttribute('data-nom-birthday-watched')) return;
    content.setAttribute('data-nom-birthday-watched', '');

    /* Rivo rebuilds the profile and the form on every navigation and after a
       save. Re-applying on mutation cannot loop: every change made here is
       guarded by an attribute that the next pass checks first. */
    new MutationObserver(apply).observe(content, { childList: true, subtree: true });
  }

  document.addEventListener('rivo-accounts:loaded', start);

  var tries = 0;
  var poll = root.setInterval(function () {
    tries += 1;
    if (alpine() && document.querySelector('.rivo-slideout__content')) {
      root.clearInterval(poll);
      start();
      return;
    }
    /* ~10s. Past that Rivo is not on this page and there is nothing to wait for. */
    if (tries > 40) root.clearInterval(poll);
  }, 250);
})(typeof window !== 'undefined' ? window : globalThis);
